import { useState, useEffect, useMemo } from 'react';
import type { Folder, DataSource } from '../types';
import { folderService } from '../services/folderService';
import { validateFolderName, FolderNameValidationResult } from '../utils/validation/folderValidation';

export interface FolderWithCount extends Omit<Folder, 'dataObjectCount'> {
    dataObjectCount: number;
}

export interface UseFoldersResult {
    folders: FolderWithCount[];
    loading: boolean;
    error: string | null;
    createFolder: (name: string) => Promise<FolderNameValidationResult & { folder?: Folder }>;
    deleteFolder: (id: number) => Promise<void>;
    validateName: (name: string) => FolderNameValidationResult;
}

export function useFolders(dataSources: DataSource[], preloaded?: Folder[]): UseFoldersResult {
    const [folders, setFolders] = useState<Folder[]>(preloaded ?? []);
    const [loading, setLoading] = useState(!preloaded);
    const [error, setError] = useState<string | null>(null);

    // Derive folder counts from dataSources
    const foldersWithCounts: FolderWithCount[] = useMemo(() => {
        return folders.map(folder => ({
            ...folder,
            dataObjectCount: dataSources.filter(ds => ds.folderId === folder.id).length
        }));
    }, [folders, dataSources]);

    // Load initial folders only if no preloaded data is provided
    useEffect(() => {
        // If preloaded is undefined (not provided at all), fetch from API
        // If preloaded is an array (even empty), don't fetch - use the preloaded data
        if (preloaded !== undefined) return; // skip fetch when preloaded is provided

        let cancelled = false;
        const loadFolders = async () => {
            try {
                setLoading(true);
                setError(null);
                const loadedFolders = await folderService.getFolders();
                if (!cancelled) setFolders(loadedFolders);
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load folders');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadFolders();
        return () => { cancelled = true; };
    }, [preloaded]);

    // Update folders when preloaded folders change
    useEffect(() => {
        if (preloaded !== undefined) {
            setFolders(preloaded);
            setLoading(false);
        }
    }, [preloaded]);

    const validateName = (name: string): FolderNameValidationResult => {
        const existingNames = folders.map(f => f.name);
        return validateFolderName(name, existingNames);
    };

    const createFolder = async (name: string): Promise<FolderNameValidationResult & { folder?: Folder }> => {
        const validation = validateName(name);
        if (!validation.valid) {
            return validation;
        }

        try {
            const newFolder = await folderService.createFolder(validation.normalized);
            setFolders(prev => [...prev, newFolder]);
            return { ...validation, folder: newFolder };
        } catch (err) {
            console.error('[useFolders] Failed to create folder', err);
            setError(err instanceof Error ? err.message : 'Failed to create folder');
            return {
                valid: false,
                normalized: validation.normalized,
                errors: {}
            };
        }
    };

    const deleteFolder = async (id: number): Promise<void> => {
        try {
            // Find the folder to get its UUID
            const folder = folders.find(f => f.id === id);
            if (!folder) {
                throw new Error('Folder not found');
            }

            // Use the UUID if available, otherwise fall back to numeric ID
            const deleteId = folder.uuid || id;
            await folderService.deleteFolder(deleteId);
            setFolders(prev => prev.filter(f => f.id !== id));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete folder');
            throw err;
        }
    };

    return {
        folders: foldersWithCounts,
        loading,
        error,
        createFolder,
        deleteFolder,
        validateName
    };
}